import React from 'react';
import ReactDOM from 'react-dom';
import { BlockEditor } from '../components/BlockEditor';
import { BlockEditorRef, BlockEditorConfig } from '../types';

export class BlockEditorAPI {
  private editorRef: React.RefObject<BlockEditorRef>;
  private container: HTMLElement | null = null;
  private config: BlockEditorConfig;

  constructor(config: BlockEditorConfig = {}) {
    this.config = config;
    this.editorRef = React.createRef<BlockEditorRef>();
  }

  /**
   * Initialize the editor in a DOM element
   */
  init(target: string | HTMLElement, options: Partial<BlockEditorConfig> = {}) {
    const element = typeof target === 'string' 
      ? document.querySelector(target) 
      : target;

    if (!element) {
      throw new Error(`Target element not found: ${target}`);
    }

    const mergedConfig = { ...this.config, ...options };

    // Store container reference
    this.container = element as HTMLElement;

    // Inject theme CSS if not already injected
    this.injectThemeCSS(mergedConfig.theme || 'wordpress');

    // Create React root and render editor
    ReactDOM.render(
      React.createElement(BlockEditor, {
        ref: this.editorRef,
        content: mergedConfig.content || '',
        blocks: mergedConfig.blocks || [],
        title: mergedConfig.title || '',
        config: mergedConfig,
        onSave: mergedConfig.onSave,
        onChange: mergedConfig.onChange
      }),
      element
    );

    return this;
  }

  /**
   * Inject theme CSS into the page
   */
  private injectThemeCSS(themeName: string = 'wordpress') {
    // Check if CSS is already injected
    if (document.getElementById('block-editor-theme-css')) {
      return;
    }

    // Get theme CSS from the global themes object
    const themes = (window as any).BlockEditor?.themes;
    if (!themes) {
      console.warn('Themes not available. Placeholder text may not display correctly.');
      return;
    }

    const theme = themes[themeName] || themes.wordpress;
    
    // Create style element
    const style = document.createElement('style');
    style.id = 'block-editor-theme-css';
    style.textContent = theme.styles;
    
    // Inject into head
    document.head.appendChild(style);
  }

  /**
   * Get current content
   */
  getContent(): string {
    return this.editorRef.current?.getContent() || '';
  }

  /**
   * Set content
   */
  setContent(content: string): void {
    this.editorRef.current?.setContent(content);
  }

  /**
   * Get current blocks
   */
  getBlocks() {
    return this.editorRef.current?.getBlocks() || [];
  }

  /**
   * Set blocks
   */
  setBlocks(blocks: any[]): void {
    this.editorRef.current?.setBlocks(blocks);
  }

  /**
   * Add a new block
   */
  addBlock(blockType: string, attributes?: Record<string, any>, index?: number): void {
    this.editorRef.current?.addBlock(blockType, attributes, index);
  }

  /**
   * Update a block
   */
  updateBlock(clientId: string, attributes: Record<string, any>): void {
    this.editorRef.current?.updateBlock(clientId, attributes);
  }

  /**
   * Remove a block
   */
  removeBlock(clientId: string): void {
    this.editorRef.current?.removeBlock(clientId);
  }

  /**
   * Focus the editor
   */
  focus(): void {
    this.editorRef.current?.focus();
  }

  /**
   * Blur the editor
   */
  blur(): void {
    this.editorRef.current?.blur();
  }

  /**
   * Save the content
   */
  save(): void {
    this.editorRef.current?.save();
  }

  /**
   * Destroy the editor
   */
  destroy(): void {
    if (this.container) {
      ReactDOM.unmountComponentAtNode(this.container);
      this.container = null;
    }
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<BlockEditorConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Global API for non-React usage
declare global {
  interface Window {
    BlockEditor: typeof BlockEditorAPI;
  }
}

// Make it available globally
if (typeof window !== 'undefined') {
  window.BlockEditor = BlockEditorAPI;
}
